import CallIcon from '@mui/icons-material/Call';
import MenuIcon from '@mui/icons-material/Menu';
import Grid from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import { TextLogo } from '@packages/shared-ui/components';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import type { CTAContent } from '@/components/Elements/CTA';
import Button, {
  type ButtonContent,
  ButtonVariant,
} from '@/components/OldButton/OldButton';
import {
  type HeaderContent,
  HeaderVariant,
} from '@/contentful/contentful-exports';
import useDeviceWidth from '@/hooks/deviceWidth';
import { trackEvents } from '@/utils/analytics';

import useStyles from './Header.styles';
import Navigation from './Navigation';
export interface Props {
  content?: HeaderContent;
}

/**
 * @deprecated use Global Navigation component
 */
const LegacyHeader: React.FC<Props> = ({ content }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredAnchor, setHoveredAnchor] = useState<number | null>(null);
  const router = useRouter();
  const { classes } = useStyles();
  const { isLargeOrXLarge } = useDeviceWidth();
  const phoneNumber = content?.fields?.cta2;
  const quoteButton =
    content?.fields?.cta?.sys?.contentType?.sys?.id === 'button'
      ? (content?.fields?.cta as ButtonContent)
      : undefined;
  const quoteCTA =
    content?.fields?.cta?.sys?.contentType?.sys?.id === 'cta'
      ? (content?.fields?.cta as CTAContent)
      : undefined;
  const appearance =
    content?.fields?.appearance || HeaderVariant['Primary Blue'];
  const appearanceClass = appearance.split(' ').join('') as
    | 'LightOverlay'
    | 'DarkOverlay'
    | 'PrimaryBlue';
  const [scroll, setScroll] = useState(false);

  const handleClick = (index: number) => {
    handleAnchorEnter(index);
  };

  const handleClose = () => {
    handleAnchorLeave();
  };

  const handleAnchorEnter = (index: number) => {
    setHoveredAnchor(index);
  };

  const handleAnchorLeave = () => {
    setHoveredAnchor(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScroll(window.scrollY > 10);
    };

    const handleDocumentClick = (event: MouseEvent) => {
      // Close the menu when clicking outside of the header component
      const headerElement = document.getElementById('header');
      if (headerElement && !headerElement.contains(event.target as Node)) {
        setHoveredAnchor(null);
      }
    };

    const handleDocumentMouseEnter = () => {
      // Close the menu when the mouse enters the document
      if (!hoveredAnchor) {
        setHoveredAnchor(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('mouseenter', handleDocumentMouseEnter);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('mouseenter', handleDocumentMouseEnter);
    };
  }, [hoveredAnchor]);

  const handleMenuButtonClick = () => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  return (
    // biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome
    <header
      id='header'
      className={`${classes.header} ${scroll ? classes.fixed : ''} ${
        classes[appearanceClass]
      }`}
    >
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        wrap='nowrap'
        spacing={0}
      >
        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
          {/** biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome */}
          <a href='/'>
            <TextLogo
              color={appearanceClass === 'DarkOverlay' ? '#000' : '#FFF'}
              width='110'
              classname={classes.logo}
            />
          </a>
          {isLargeOrXLarge &&
            content?.fields?.categories.map((category, index) => (
              // biome-ignore lint/a11y/noStaticElementInteractions: Automated migration to Biome
              <div
                className={classes.menuCategory}
                key={category.sys.id}
                onMouseEnter={() => handleClick(index)}
                onMouseLeave={() => handleClose()}
              >
                <span className={classes.categoryItem}>
                  {category.fields.name}
                </span>
                <ul className={classes.menuList}>
                  {category.fields.links.map((link) => (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: Automated migration to Biome
                    <li
                      key={link.sys.id}
                      onClick={() => {
                        handleClose();
                        trackEvents({
                          event: 'Nav click',
                          click_text_category: category.fields.name,
                          click_text: link.fields.text,
                        });
                        router.push(link.fields.url);
                      }}
                    >
                      {/** biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome */}
                      <a href={link.fields.url}>{link.fields.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </Grid>
        <Grid item>
          {!isLargeOrXLarge ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {phoneNumber && (
                <IconButton
                  href={phoneNumber.fields.url}
                  className={classes.phoneIcon}
                  onClick={() => {
                    trackEvents({
                      event: 'Nav click',
                      click_text_category: 'Top level',
                      click_text: phoneNumber.fields.text,
                    });
                  }}
                >
                  <CallIcon
                    className={`${
                      appearance === HeaderVariant['Dark Overlay']
                        ? classes.dark
                        : classes.light
                    }`}
                  />
                </IconButton>
              )}
              {/** biome-ignore lint/a11y/useButtonType: Automated migration to Biome */}
              <button
                className={classes.menuIcon}
                onClick={handleMenuButtonClick}
              >
                <MenuIcon
                  className={`${
                    appearance === HeaderVariant['Dark Overlay']
                      ? classes.dark
                      : classes.light
                  }`}
                />
              </button>
              <Navigation
                content={content}
                open={isMenuOpen}
                onClose={handleMenuClose}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {phoneNumber && (
                // biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome
                <a
                  className={classes.phoneLink}
                  href={phoneNumber.fields.url}
                  onClick={() => {
                    trackEvents({
                      event: 'Nav click',
                      click_text_category: 'Top level',
                      click_text: phoneNumber.fields.text,
                    });
                  }}
                >
                  {phoneNumber.fields.text}
                </a>
              )}
              {quoteButton && <Button {...quoteButton.fields} />}
              {quoteCTA && (
                <Button
                  url={quoteCTA.fields.url}
                  variant={ButtonVariant.OffWhitePill}
                  label={quoteCTA.fields.text}
                  gaEvent={{
                    event: 'Nav click',
                    click_text_category: 'Top level',
                    click_text: quoteCTA.fields.text,
                  }}
                />
              )}
            </div>
          )}
        </Grid>
      </Grid>
    </header>
  );
};

export default LegacyHeader;
